import {useMemo, useEffect, useCallback, useRef} from 'react';
import {useIntl} from '@sail/intl';
import type {
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum,
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum,
} from '@stripe-internal/data-v1/schema/types';
import {getStore, setValue} from 'src/v2/state/store';
import {useAllocateDepositAddress} from 'src/v2/components/directDeposit/useAllocateDepositAddress';
import {useUpdateRefundAddress} from 'src/v2/components/directDeposit/useUpdateRefundAddress';
import {handleError} from 'src/v2/components/error/handleError';

type CurrencyNetwork =
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum;
type AssetCode =
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum;

interface NetworkOption {
  network: CurrencyNetwork;
  isDisabled: boolean;
  compatibleCurrencies?: AssetCode[];
}

interface CurrencyOption {
  currency: AssetCode;
  isDisabled: boolean;
  compatibleNetworks?: CurrencyNetwork[];
}

export interface DirectDepositOptions {
  selectedNetwork: CurrencyNetwork | null;
  setSelectedNetwork: (network: CurrencyNetwork) => void;
  selectedCurrency: AssetCode | null;
  setSelectedCurrency: (currency: AssetCode) => void;
  networkOptions: NetworkOption[];
  currencyOptions: CurrencyOption[];
}

const clearRefundAddressState = () => {
  setValue((state) => {
    state.directDeposit.refundMethod = null;
    state.directDeposit.customRefundAddress = '';
    state.directDeposit.isRefundAddressApplied = false;
  });
};

export const useDirectDepositOptions = (): DirectDepositOptions => {
  const store = getStore();
  const intl = useIntl();
  const [updateRefundAddress] = useUpdateRefundAddress();
  const previousNetworkRef = useRef<CurrencyNetwork | null>(null);

  const supportedCurrencies = store.useSelector(
    (state) => state.supportedCurrencies,
  );

  const selectedNetwork = store.useSelector(
    (state) => state.directDeposit.selectedDirectDepositNetwork,
  );
  const selectedCurrency = store.useSelector(
    (state) => state.directDeposit.selectedDirectDepositCurrency,
  );

  const setSelectedNetwork = useCallback((network: CurrencyNetwork) => {
    setValue((state) => {
      state.directDeposit.selectedDirectDepositNetwork = network;
    });
  }, []);

  const setSelectedCurrency = useCallback((currency: AssetCode) => {
    setValue((state) => {
      state.directDeposit.selectedDirectDepositCurrency = currency;
    });
  }, []);

  // Get all networks with disabled state based on selected currency
  const networkOptions = useMemo(() => {
    const allNetworks = [
      ...new Set(supportedCurrencies.map((sc) => sc.currencyNetwork)),
    ];
    const compatibleNetworks = selectedCurrency
      ? new Set(
          supportedCurrencies
            .filter((sc) => sc.assetCode === selectedCurrency)
            .map((sc) => sc.currencyNetwork),
        )
      : null;

    return allNetworks.map((network) => {
      const isDisabled =
        compatibleNetworks !== null && !compatibleNetworks.has(network);
      const compatibleCurrencies = isDisabled
        ? [
            ...new Set(
              supportedCurrencies
                .filter((sc) => sc.currencyNetwork === network)
                .map((sc) => sc.assetCode),
            ),
          ]
        : undefined;

      return {
        network,
        isDisabled,
        compatibleCurrencies,
      };
    });
  }, [supportedCurrencies, selectedCurrency]);

  // Get all currencies with disabled state based on selected network
  const currencyOptions = useMemo(() => {
    const allCurrencies = [
      ...new Set(supportedCurrencies.map((sc) => sc.assetCode)),
    ];
    const compatibleCurrencies = selectedNetwork
      ? new Set(
          supportedCurrencies
            .filter((sc) => sc.currencyNetwork === selectedNetwork)
            .map((sc) => sc.assetCode),
        )
      : null;

    return allCurrencies.map((currency) => {
      const isDisabled =
        compatibleCurrencies !== null && !compatibleCurrencies.has(currency);
      const compatibleNetworks = isDisabled
        ? [
            ...new Set(
              supportedCurrencies
                .filter((sc) => sc.assetCode === currency)
                .map((sc) => sc.currencyNetwork),
            ),
          ]
        : undefined;

      return {
        currency,
        isDisabled,
        compatibleNetworks,
      };
    });
  }, [supportedCurrencies, selectedNetwork]);

  // Auto-correct the selected network if it becomes invalid
  // This happens when the user changes currency and the previously selected
  // network isn't available for the new currency
  useEffect(() => {
    if (selectedNetwork) {
      const selectedOption = networkOptions.find(
        (opt) => opt.network === selectedNetwork,
      );
      if (selectedOption?.isDisabled) {
        const firstEnabled = networkOptions.find((opt) => !opt.isDisabled);
        if (firstEnabled) {
          setSelectedNetwork(firstEnabled.network);
        }
      }
    }
  }, [networkOptions, selectedNetwork, setSelectedNetwork]);

  // Auto-correct the selected currency if it becomes invalid
  // This happens when the user changes network and the previously selected
  // currency isn't available for the new network
  useEffect(() => {
    if (selectedCurrency) {
      const selectedOption = currencyOptions.find(
        (opt) => opt.currency === selectedCurrency,
      );
      if (selectedOption?.isDisabled) {
        const firstEnabled = currencyOptions.find((opt) => !opt.isDisabled);
        if (firstEnabled) {
          setSelectedCurrency(firstEnabled.currency);
        }
      }
    }
  }, [currencyOptions, selectedCurrency, setSelectedCurrency]);

  // Clear refund address whenever the user switches networks, regardless of which step they're on.
  // This ensures the refund address always matches the selected network.
  useEffect(() => {
    const hasNetworkChanged =
      previousNetworkRef.current !== null &&
      previousNetworkRef.current !== selectedNetwork;

    previousNetworkRef.current = selectedNetwork;

    if (hasNetworkChanged && selectedNetwork) {
      updateRefundAddress({
        refundAddress: null,
        destinationNetwork: selectedNetwork,
      })
        .then(clearRefundAddressState)
        .catch((error) => {
          clearRefundAddressState();
          handleError({error, intl, context: 'direct_deposit'});
        });
    }
  }, [selectedNetwork, intl, updateRefundAddress]);

  return {
    selectedNetwork,
    setSelectedNetwork,
    selectedCurrency,
    setSelectedCurrency,
    networkOptions,
    currencyOptions,
  };
};

interface UseDirectDepositReturn extends DirectDepositOptions {
  depositAddress: string;
  isAllocating: boolean;
  handleClose: () => void;
}

export const useDirectDeposit = (): UseDirectDepositReturn => {
  const store = getStore();
  const intl = useIntl();
  const [updateRefundAddress] = useUpdateRefundAddress();
  const hasInitializedRef = useRef(false);

  const supportedCurrencies = store.useSelector(
    (state) => state.supportedCurrencies,
  );

  const refundMethod = store.useSelector(
    (state) => state.directDeposit.refundMethod,
  );

  const {
    selectedNetwork,
    setSelectedNetwork,
    selectedCurrency,
    setSelectedCurrency,
    networkOptions,
    currencyOptions,
  } = useDirectDepositOptions();

  // On initial QR code screen mount, clear any stale refund address from a previous session
  // (e.g. user set address on network A, refreshed, now defaults to network B).
  // Skip clearing if the user already explicitly chose a refund method in the email step
  // (refundMethod !== null means the user made a deliberate selection).
  useEffect(() => {
    if (!hasInitializedRef.current && selectedNetwork) {
      hasInitializedRef.current = true;

      if (refundMethod !== null) {
        return;
      }

      updateRefundAddress({
        refundAddress: null,
        destinationNetwork: selectedNetwork,
      })
        .then(clearRefundAddressState)
        .catch((error) => {
          clearRefundAddressState();
          handleError({error, intl, context: 'direct_deposit'});
        });
    }
  }, [selectedNetwork, refundMethod, intl, updateRefundAddress]);

  const {address: allocatedAddress, isAllocating} = useAllocateDepositAddress({
    network: selectedNetwork,
    currency: selectedCurrency,
  });

  const selectedSupportedCurrency = useMemo(
    () =>
      supportedCurrencies.find(
        (currency) =>
          currency.currencyNetwork === selectedNetwork &&
          currency.assetCode === selectedCurrency,
      ),
    [supportedCurrencies, selectedNetwork, selectedCurrency],
  );

  const depositAddress = useMemo(() => {
    if (!selectedNetwork || !selectedCurrency) {
      return '';
    }
    if (!selectedSupportedCurrency) {
      throw new Error(
        `No supported currency found for network: ${selectedNetwork}, currency: ${selectedCurrency}`,
      );
    }

    // Always use on-demand allocation (flag is fully rolled out)
    return allocatedAddress;
  }, [
    selectedSupportedCurrency,
    allocatedAddress,
    selectedNetwork,
    selectedCurrency,
  ]);

  const handleClose = useCallback(() => {
    setValue((state) => {
      state.screen = 'WALLET_SELECT';
    });
  }, []);

  return {
    selectedNetwork,
    setSelectedNetwork,
    selectedCurrency,
    setSelectedCurrency,
    networkOptions,
    currencyOptions,
    depositAddress,
    isAllocating,
    handleClose,
  };
};
